import { Box, Button, IconButton, Modal, Tab, Tabs, Typography } from '@mui/material';
import PayOutForm from './PayOutForm';
import ArrowBackIcon from '@mui/icons-material/ArrowBack';
import CloseIcon from '@mui/icons-material/Close';
import { getFields, getMetadata } from './payoutHelpers';
import { useEffect, useState } from 'react';
import { getCountries, useJunoCommonFunctions } from '../../../../client/Juno/helpers';
import Attachments from './Attachments';
import PropTypes from 'prop-types';
import PayoutConfirm from './PayoutConfirm';
import SavedAccounts from './SavedAccounts';
import { useSelector } from 'react-redux';
import JunoButton from '../../common/JunoButton';

const tabs = ['new account', 'saved accounts'];

const PayOutModal = ({
    type = 'withdrawal',
    onBack,
    onClose,
    formProps,
    modals,
    setModals,
    openedFromForm,
    selectedAccount,
    fromAddress,
    setSelectedAccountData,
    selectedAccountReqMissing,
}) => {
    const { useWindowWidth } = useJunoCommonFunctions();
    const windowWidth = useWindowWidth();

    const style = {
        position: 'absolute',
        top: '50%',
        left: '50%',
        transform: 'translate(-50%, -50%)',
        width: windowWidth < 716 ? '100vw' : '90%',
        maxWidth: windowWidth < 716 ? '100vw' : 550,
        bgcolor: 'var(--common-modal-bg)',
        boxShadow: 24,
        borderRadius: windowWidth < 716 ? 0 : 2,
        p: 3,
        maxHeight: windowWidth < 716 ? '100vh' : '90vh',
        display: 'flex',
        flexDirection: 'column',
        overflow: 'hidden',
        ...(windowWidth < 716 && { height: '100vh', p: 2 }),
    };
    const { list: typeSpecificFields } = getFields(type, 'all');

    const { heading } = getMetadata(type);
    const savedAccounts = useSelector(state => state.juno.savedAccounts);
    const [activeTab, setActiveTab] = useState(
        savedAccounts?.length && !openedFromForm && !selectedAccount ? 1 : 0,
    );
    const [activeTabText, setActiveTabText] = useState(savedAccounts?.length && !openedFromForm && !selectedAccount ?  'saved accounts' : 'new account');
    const [countries, setCountries] = useState([]);
    const [attachments, setAttachments] = useState({
        files: [],
        errors: '',
    });
    const [accountSelected, setAccountSelected] = useState(selectedAccount || false);
    const { balancesCurrencies = [] } = useSelector(state => state.juno);

    const fetchCountries = async () => {
        try {
            let countriesList = await getCountries();
            setCountries(countriesList);
        } catch (error) {
            console.error(error);
        }
    };

    useEffect(() => {
        fetchCountries();
    }, []);

    const handleSavedClick = account => {
        formProps.setFormData(prev => ({
            ...prev,
            ...account,
            selectedCurrency: balancesCurrencies?.find(
                currency => currency.currencyShortName == account?.currency,
            ),
        }));
        setActiveTab(0);
        setSelectedAccountData(account);
        setAccountSelected(true);
    };

    useEffect(() => {
        if (!savedAccounts.length) {
            setActiveTab(0);
            setActiveTabText('new account')
        }
    }, [savedAccounts]);

    const areAllRequiredFieldsFilled  = typeSpecificFields.filter(item => {        
        if (formProps.formData.save) {

            return item.hasOwnProperty('hasError') 
        }
        else {
            return item.hasOwnProperty('hasError') && item.dataKey !== 'accountNickName'
        }
    }).every(field => {
        const value = formProps?.formData?.[field.name];
        return value !== undefined && value.toString().trim() !== ''
    })

    return (
        <>
            <Modal open={modals.openMainModal} onClose={onClose}>
                <Box sx={style}>
                    <Box className="flex justify-between" alignItems="center">
                        <div className="flex items-center">
                            {(onBack || accountSelected) && (
                                <IconButton
                                    onClick={
                                        fromAddress
                                            ? onClose
                                            : accountSelected
                                                ? () => {
                                                    setAccountSelected(false);
                                                    setActiveTab(1);
                                                    setActiveTabText('saved accounts')
                                                    formProps.setFormData({});
                                                }
                                                : onBack
                                    }
                                    sx={{ mr: 1, pl: 0 }}>
                                    <ArrowBackIcon className="!text-primary text-xl font-semibold" />
                                </IconButton>
                            )}
                            <Typography
                                className="!text-primary text-xl text-uppercase font-bold !font-inter"
                                variant="h6"
                                component="h2"
                                id="transaction-modal-title">
                                {heading}
                            </Typography>
                        </div>

                        {onClose && (
                            <IconButton onClick={onClose} aria-label="close">
                                <CloseIcon className="!text-primary" />
                            </IconButton>
                        )}
                    </Box>

                    {savedAccounts?.length && !accountSelected ? (
                        <Box>
                            <Tabs className="bg-card-bg mt-4 mb-7 rounded-lg outline outline-[0.50px] outline-black/5">
                                {tabs.map((tab, index) => (
                                    <Tab
                                        key={index}
                                        label={tab}
                                        wrapped
                                        data-e2e={tab}
                                        onClick={() => {
                                            formProps?.setFormData(prev => ({
                                                selectedCurrency: prev?.selectedCurrency,
                                            }));
                                            formProps?.setFormFieldErrors({});
                                            setActiveTab(index);
                                            setActiveTabText(index ? 'saved accounts' :'new account')
                                        }}
                                        sx={{
                                            flexGrow: 1,
                                            minWidth: activeTab === index ? 50 : 'auto',
                                            borderRadius: '7px',
                                            background:
                                                activeTab === index ? '#FCFCFC' : 'transparent',
                                            color:
                                                activeTab === index
                                                    ? '#18181B'
                                                    : 'var(--balance-manage-text)',
                                            fontWeight: 500,
                                            fontSize: '14px',
                                            lineHeight: '20px',
                                            paddingX: '16px',
                                            height: '10px',
                                            boxShadow:
                                                activeTab == index
                                                    ? '0px 3px 1px 0px rgba(0, 0, 0, 0.04), 0px 3px 8px 0px rgba(0, 0, 0, 0.12);'
                                                    : '',
                                        }}
                                    />
                                ))}
                            </Tabs>
                        </Box>
                    ) : null}
                    {activeTab == 0 ? (
                        <>
                            <Box
                                className={`custom-scrollbar ${!savedAccounts?.length || accountSelected ? 'mt-3' : ''} !h-[90vh] md:!h-[490px]`}>
                                <PayOutForm
                                    {...formProps}
                                    type={type}
                                    isModal={true}
                                    selectedAccountReqMissing={selectedAccountReqMissing}
                                    typeSpecificFields={typeSpecificFields}
                                    countries={countries}
                                    accountSelected={accountSelected}
                                />
                                <div className="border-t border-[#D1D1D6] my-4 dark:border-[#525252]" />
                                <Attachments
                                    attachments={attachments}
                                    setAttachments={setAttachments}
                                />
                            </Box>
                            <div className="border-t border-[#D1D1D6] -mx-6 dark:border-[#525252] " />
                            <Box>
                                <JunoButton
                                    className="w-full mt-4 mb-1 !px-8 !py-4 text-sm"
                                    dataE2e="continue-btn"
                                    onClick={() => {
                                        let noErrors =
                                            formProps.validateAll(typeSpecificFields);
                                        if (noErrors) {
                                            setModals(prev => ({
                                                ...prev,
                                                openConfirmModal: true,
                                                openMainModal: false,
                                            }));
                                        }
                                    }}
                                    disabled={
                                     activeTabText == 'saved accounts' ? !formProps?.formData?.fiatAmount : !areAllRequiredFieldsFilled  || !formProps?.formData?.fiatAmount
                                    }>
                                    Continue
                                </JunoButton>

                            </Box>
                        </>
                    ) : (
                        <SavedAccounts onClick={handleSavedClick} />
                    )}
                </Box>
            </Modal>
            {modals.openConfirmModal ? (
                <PayoutConfirm
                    open={modals.openConfirmModal}
                    onClose={() => {
                        setModals(prev => ({
                            ...prev,
                            openConfirmModal: false,
                            openMainModal: false,
                        }));
                        onClose();
                    }}
                    onBack={() =>
                        setModals(prev => ({
                            ...prev,
                            openConfirmModal: false,
                            openMainModal: true,
                        }))
                    }
                    formData={formProps?.formData}
                    title={heading}
                    handleCreate={formProps?.handleCreate}
                    attachments={attachments}
                />
            ) : null}
        </>
    );
};

PayOutModal.propTypes = {
    type: PropTypes.oneOf(['withdrawal', 'paymentOut']),
    onBack: PropTypes.func,
    onClose: PropTypes.func,
    onSubmit: PropTypes.func,
    formData: PropTypes.object,
    open: PropTypes.bool,
};

export default PayOutModal;
